import React, {
    useContext, useState, useCallback, useMemo, useEffect
} from 'react';
import classNames from 'classnames';
import {
    Tooltip,
    StepProgressBar
} from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardContext } from 'gw-portals-wizard-react';
import { WizardSidebar } from 'gw-portals-wizard-components-ui';
import ButtonWithLoader from '../components/ButtonWithLoader/ButtonWithLoader';

import styles from './AdacDigitalWizardPageTemplate.module.scss';
import messages from './AdacDigitalWizardPageTemplate.messages';

function AdacDigitalWizardPageTemplate(props) {
    const translator = useTranslator();
    const breakpoint = useContext(BreakpointTrackerContext);
    const wizardContext = useContext(WizardContext);
    const { steps, currentStep } = wizardContext;
    const [stepsForProgressBar, setStepsForProgressBar] = useState([]);
    const isPhone = breakpoint === 'phone';
    const [showSidebar, setShowSidebar] = useState(false);
    const [showMobileSteps, setShowMobileSteps] = useState(false);
    const {
        children: content,
        onNext,
        isLoadingNext,
        disableNext,
        showNext,
        nextLabel,
        nextButtonTooltip,
        onPrevious,
        isLoadingPrevious,
        disablePrevious,
        showPrevious,
        previousLabel,
        onCancel,
        isLoadingCancel,
        disableCancel,
        showCancel,
        cancelLabel,
        renderContextComponent,
        renderWidgets,
        wizardData,
        wizardSnapshot,
        showContextComponent,
        showWizardHeader,
        customMessageModal,
        hideWizardSteps
    } = props;

    useEffect(() => {
        if (hideWizardSteps) return;
        switch (breakpoint) {
            case 'phone':
                setShowSidebar(false);
                setShowMobileSteps(true);
                break;
            default:
                setShowSidebar(true);
                setShowMobileSteps(false);
                break;
        }
    }, [breakpoint, hideWizardSteps]);

    useEffect(() => {
        const stepsToSet = steps.map((step) => {
            return {
                active: step.path === currentStep.path,
                // do nothing as we do not want to navigate on Progress Bar
                onClick: () => {},
                title: translator(step.title),
                visited: step.submitted
            };
        });
        setStepsForProgressBar(stepsToSet);
    }, [currentStep.path, steps, translator]);

    const contextComponent = useCallback(() => {
        return <div className={styles.wizardContext}>{renderContextComponent(wizardSnapshot)}</div>;
    }, [renderContextComponent, wizardSnapshot]);

    const shouldShowButtons = useMemo(() => !(
        showNext === false
        && showPrevious === false
        && showCancel === false
    ), [showCancel, showNext, showPrevious]);

    const isPerformingTransiton = useMemo(() => (
        isLoadingCancel || isLoadingPrevious || isLoadingNext
    ), [isLoadingCancel, isLoadingNext, isLoadingPrevious]);

    const wizardClassName = classNames(styles.wizardMain, {
        [styles.wizardMainShowSidebar]: showSidebar && isPhone
    });

    const nextButton = useMemo(() => (
        <ButtonWithLoader
            id="nextBtn"
            className={showPrevious ? styles.nextbutton : styles.nextButtonFullWidth}
            disabled={disableNext || isPerformingTransiton}
            onClick={onNext}
            isLoading={isLoadingNext}
            label={translator(nextLabel)}
        />
    ), [
        disableNext,
        isLoadingNext,
        isPerformingTransiton,
        nextLabel,
        onNext,
        showPrevious,
        translator
    ]);

    const nextButtonWithTooltip = useMemo(() => (
        <Tooltip placement="bottom" content={nextButtonTooltip}>
            <span>{nextButton}</span>
        </Tooltip>
    ), [nextButton, nextButtonTooltip]);

    const renderNextButton = nextButtonTooltip ? nextButtonWithTooltip : nextButton;
    const buttonContainerClassName = classNames(styles.navTransitionButtons, {
        [styles.navTransitionButtonOnlyNext]: !showPrevious && isPhone
    });

    return (
        <div className={styles.digitalWizard}>
            {showContextComponent && contextComponent()}
            <div className={wizardClassName}>
                {!isPhone && showSidebar ? (
                    <div className={styles.wizardNavigation}>
                        <WizardSidebar customMessageModal={customMessageModal} />
                        <div className={styles.wizardWidgets}>
                            {renderWidgets(wizardData)}
                        </div>
                    </div>
                ) : null}
                <div className={styles.wizardContent}>
                    <h1 className="pageHeaderTitle">{translator(messages.pageHeaderTitle)}</h1>
                    {isPhone && showMobileSteps
                        ? (
                            <div className={styles.progressBarContainer}>
                                <StepProgressBar
                                    steps={stepsForProgressBar}
                                    className={styles.progressBar}
                                />
                            </div>
                        ) : null}
                    {showWizardHeader && contextComponent()}
                    {content}
                    <div className={classNames({
                        [styles.navButtons]: shouldShowButtons
                    })}
                    >
                        {showCancel && (
                            <ButtonWithLoader
                                disabled={disableCancel || isPerformingTransiton}
                                type="tertiary"
                                onClick={onCancel}
                                label={translator(cancelLabel)}
                                isLoading={isLoadingCancel}
                            />
                        )}
                        <div className={buttonContainerClassName}>
                            {showPrevious && (
                                <ButtonWithLoader
                                    disabled={disablePrevious || isPerformingTransiton}
                                    type="secondary"
                                    onClick={onPrevious}
                                    label={translator(previousLabel)}
                                    isLoading={isLoadingPrevious}
                                />
                            )}
                            {showNext && renderNextButton}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

AdacDigitalWizardPageTemplate.defaultProps = {
    renderContextComponent: () => null,
    renderWidgets: () => null,
    showContextComponent: true,
    showWizardHeader: false,
    nextButtonTooltip: ''
};

export default AdacDigitalWizardPageTemplate;
